import React, { useEffect, useState } from "react";
import { logResponse } from "../utils/logger";
import { isTestEnv } from "../utils/env";
import { fetchGetSubscriptions, fetchGetCustomer } from "../api";
import { splitKana, splitBirthDate, getTelType } from "../utils/orderPrefill";
import iconCalendar from "../assets/img/icon_calendar.svg";
import iconChange from "../assets/img/icon_change.svg";
import iconPointOrange from "../assets/img/icon_point_orange.svg";
import itemZakuro from "../assets/img/item_zakuro.png";
import itemDx from "../assets/img/item_dx.png";
import itemBifi from "../assets/img/item_bifi.png";
import itemJulee from "../assets/img/item_julee.png";
import itemJuleeFive from "../assets/img/item_julee_five.png";
import itemZpreg from "../assets/img/item_zpreg.png";
import itemFiber from "../assets/img/item_fiber.png";
import itemPururun from "../assets/img/item_pururun.png";
import itemMiniDx from "../assets/img/item_mini_dx.png";
import itemMiniZakuro from "../assets/img/item_mini_zakuro.png";
import itemMiniZpreg from "../assets/img/item_mini_zpreg.png";
import itemSample from "../assets/img/pic_item_sample.png";

const getProductUnit = (productName) => {
  if (productName.includes("ジュレ") && productName.includes("5包入り")) return "袋";
  if (productName.includes("ジュレ")) return "箱";
  if (productName.includes("ビフィずっと")) return "袋";
  if (productName.includes("山野草ファイバー")) return "袋";
  if (productName.includes("ぷるるん酵素")) return "袋";
  return "本";
};

const getProductImage = (productName) => {
  // 複合キーワード（ミニボトル×商品）を先に判定
  if (productName.includes("ミニボトル") && productName.includes("ブルーベリー")) return itemMiniDx;
  if (productName.includes("ミニボトル") && productName.includes("若榴da檸檬")) return itemMiniZakuro;
  if (productName.includes("ミニボトル") && productName.includes("ZAKURO")) return itemMiniZpreg;
  if (productName.includes("ジュレ") && productName.includes("5包入り")) return itemJuleeFive;
  // 単一キーワード（ジュレは「若榴da檸檬」を含むため先に判定）
  if (productName.includes("ブルーベリー")) return itemDx;
  if (productName.includes("ジュレ")) return itemJulee;
  if (productName.includes("若榴da檸檬")) return itemZakuro;
  if (productName.includes("ビフィずっと")) return itemBifi;
  if (productName.includes("ZAKURO")) return itemZpreg;
  if (productName.includes("山野草ファイバー")) return itemFiber;
  if (productName.includes("ぷるるん酵素")) return itemPururun;
  return itemSample;
};
const getLpUrl = (productName) => {
  const base = isTestEnv()
    ? "https://www.kotohogi.co.jp/test"
    : "https://www.kotohogi.co.jp";
  // 判定順は getProductImage と同じ（ジュレは「若榴da檸檬」を含むため先に判定）
  if (productName.includes("ブルーベリー")) {
    return `${base}/lps/blueberry/dx001/?ad_id=26080301&ad_type=my_page_dx#input_form`;
  }
  if (productName.includes("ジュレ")) {
    return `${base}/lps/zakuro/gel003/?ad_id=26080303&ad_type=my_page_gel003#input_form`;
  }
  if (productName.includes("若榴da檸檬")) {
    return `${base}/lps/zakuro/nk001/?ad_id=26080302&ad_type=my_page_zk#input_form`;
  }
  if (productName.includes("ビフィずっと")) {
    return `${base}/lps/bifi/bifizutto/?ad_id=26080305&ad_type=my_page_bzs_01#input_form`;
  }
  if (productName.includes("ZAKURO")) {
    return `${base}/lps/zakuro/0003b/?ad_id=26080304&ad_type=my_page_air-zn-1#input_form`;
  }
  return null;
};

const groupBySubscriptionNumber = (items) => {
  const grouped = {};
  items.forEach((item) => {
    const key = item.subscription_number;
    if (!grouped[key]) grouped[key] = { subscription_number: key, items: [] };
    grouped[key].items.push(item);
  });
  return Object.values(grouped);
};

// ★ next_shipping_date の空判定（Order.jsx と同じロジック）
const isEmptyDate = (value) => {
  if (!value) return true; // null, undefined, ""
  if (value === "0000-00-00") return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
};

const Regular = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openedDetailIndex, setOpenedDetailIndex] = useState(null);

  // ★ 追加：customer 情報
  const [customer, setCustomer] = useState(null);

  const handleDetailToggle = (index) => {
    setOpenedDetailIndex((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    logResponse("token:", token);

    fetchGetSubscriptions(token)
      .then((data) => {
        logResponse("res:", data);
        if (Array.isArray(data)) {
          const grouped = groupBySubscriptionNumber(data);
          setSubscriptions(grouped);
        } else {
          console.warn("定期情報の形式が不正です:", data);
        }
      })
      .catch((err) => {
        console.error("定期情報の取得に失敗しました:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  // ★ 追加：customer を取得
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    fetchGetCustomer(token).then((res) => {
      setCustomer(res);
      logResponse("customer:", res);
    });
  }, []);

  return (
    <section className="section regular-section">
      <h2 className="section-title">
        <i><img src={iconCalendar} alt="カレンダーアイコン" /></i>
        定期内容
      </h2>

      {loading ? (
        <p>読み込み中です...</p>
      ) : (
        <div className="order-group group-wrapper">
          {subscriptions.map((group, index) => {
            const firstItem = group.items[0];

            return (
              <div className="order" key={group.subscription_number}>
                <div className="order-status flex middle left">
                  <h3 className="date">
                    {firstItem.next_delivery_date
                      ? `${firstItem.next_delivery_date} 到着予定`
                      : "お届け日未定"}
                  </h3>

                  {firstItem.is_unpaid === "unpaid" && (
                    <span className="status unpaid">未入金</span>
                  )}

                  {firstItem.end_date && (
                    <span className="status returned">解約済</span>
                  )}
                </div>

                {group.items.map((item, i) => (
                  <div className="item" key={i}>
                    <div className="flex">
                      <div className="image">
                        <img src={getProductImage(item.product_name)} alt={item.product_name} />
                      </div>
                      <div className="info">
                        <ul className="item-info-list">
                          <li>{item.product_name}</li>
                          <li>数量：{item.quantity}{getProductUnit(item.product_name)}（{item.cycle}）</li>
                          <li>単価：{item.unit_price.toLocaleString()}円（税込）</li>
                          <li>総額：{item.line_amount.toLocaleString()}円（税込）</li>
                        </ul>

                        {/* ★ 再購入リンク（解約済 & customer 取得済 のときだけ表示） */}
                        {firstItem.end_date && customer && (() => {
                          // 商品名 → LP URL
                          const lpUrl = getLpUrl(item.product_name);
                          if (!lpUrl) return null;

                          // 氏名をスペースで分割
                          const [sei = "", mei = ""] = (customer.customer_name || "").split(/\s+/);

                          // フリガナ（半角カナ→全角カタカナ）・生年月日を注文ページ用に整形
                          const { seikana, meikana } = splitKana(customer.customer_kana);
                          const { year, month, day } = splitBirthDate(customer.birth_date);

                          // 電話番号を3分割
                          const tel = customer.phone_number_1 || "";
                          const [tel1 = "", tel2 = "", tel3 = ""] = tel.split("-");

                          // 電話種別（LPの tel_type）は顧客APIに無いため電話番号から導出（080/090→携帯・他→自宅）
                          const telType = getTelType(customer.phone_number_1);

                          return (
                            <form
                              method="POST"
                              action={lpUrl}
                              style={{ marginTop: "10px" }}
                            >
                              {/* ▼ 氏名 */}
                              <input type="hidden" name="sei" value={sei} />
                              <input type="hidden" name="mei" value={mei} />

                              {/* ▼ フリガナ（全角カタカナに変換） */}
                              <input type="hidden" name="seikana" value={seikana} />
                              <input type="hidden" name="meikana" value={meikana} />

                              {/* ▼ 生年月日 */}
                              <input type="hidden" name="year" value={year} />
                              <input type="hidden" name="month" value={month} />
                              <input type="hidden" name="day" value={day} />

                              {/* ▼ 性別（顧客APIの gender_type をそのまま／未取得なら空＝LPデフォルト維持） */}
                              <input type="hidden" name="gender" value={customer.gender_type || ""} />

                              {/* ▼ 電話種別（電話番号から導出：080/090→携帯・他→自宅） */}
                              <input type="hidden" name="tel_type" value={telType} />

                              {/* ▼ 郵便番号 */}
                              <input type="hidden" name="zip" value={customer.postal_code} />

                              {/* ▼ 住所 */}
                              <input type="hidden" name="add1" value={customer.prefecture_name} />
                              <input type="hidden" name="add2" value={customer.municipality_name} />
                              <input
                                type="hidden"
                                name="add3"
                                value={`${customer.town_name} ${customer.address_number}`}
                              />
                              <input
                                type="hidden"
                                name="add4"
                                value={`${customer.address_detail_1} ${customer.address_detail_2}`}
                              />

                              {/* ▼ 電話番号 */}
                              <input type="hidden" name="tel1" value={tel1} />
                              <input type="hidden" name="tel2" value={tel2} />
                              <input type="hidden" name="tel3" value={tel3} />

                              {/* ▼ メール */}
                              <input type="hidden" name="email" value={customer.email} />
                              <input type="hidden" name="email2" value={customer.email} />

                              {/* ▼ GMO MemberID */}
                              <input type="hidden" name="account" value={customer.member_id_1 || ""} />
                              <input type="hidden" name="account2" value={customer.member_id_2 || ""} />

                              {/* ▼ ボタン（リンク風） */}
                              <button
                                type="submit"
                                className="underline bold"
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                  color: "#EF8200",
                                  fontSize: "14px",
                                }}
                              >
                                この商品をもう一度注文する
                              </button>
                            </form>
                          );
                        })()}
                      </div>
                    </div>

                    {i < group.items.length - 1 && (
                      <svg className="item-separate" width="100%" height="10">
                        <line
                          x1="0"
                          y1="5"
                          x2="100%"
                          y2="5"
                          stroke="#56331f"
                          strokeWidth="1"
                          strokeDasharray="10, 5"
                        />
                      </svg>
                    )}
                  </div>
                ))}

                <div className="order-detail">
                  <p>商品合計：{firstItem.total_amount.toLocaleString() || 0}円（税込）</p>
                  <p>送料：{firstItem.shipping_amount.toLocaleString() || 0}円</p>
                  <p>ご請求金額：{firstItem.billing_amount.toLocaleString() || 0}円（税込）</p>

                  <p>支払い方法：
                    <a href={`/payment/${firstItem.subscription_number}`} className="underline">
                      {firstItem.payment_method_name}
                    </a>
                  </p>

                  <p>ポイント利用：
                    {firstItem.point_usage_flag === "する" && firstItem.usage_points > 0
                      ? `${firstItem.usage_points}ポイントたまったら利用する`
                      : "利用しない"}
                  </p>

                  <p>お届け予定日：{firstItem.next_delivery_date || "未定"}</p>
                  <p>お届け時間：{firstItem.delivery_time_name || "時間指定なし"}</p>

                  <p>
                    お届け先：
                    <span
                      className={`item-info-detail-btn link underline ${openedDetailIndex === index ? "open" : ""}`}
                      onClick={() => handleDetailToggle(index)}
                    >
                      {firstItem.recipient_name}
                    </span>
                  </p>

                  <p className={`item-info-detail-body ${openedDetailIndex === index ? "open" : ""}`}>
                    〒{firstItem.recipient_postal}
                    <br />
                    {firstItem.recipient_prefecture_name} {firstItem.recipient_municipality_name} {firstItem.recipient_town_name}
                    <br />
                    {firstItem.recipient_address_number} {firstItem.recipient_address_detail_1} {firstItem.recipient_address_detail_2}
                  </p>

                  {/* ★ next_shipping_date が「実質的に空」のときだけ表示 */}
                  {isEmptyDate(firstItem.next_shipping_date) && (
<div className="change-group">
  <i><img src={iconChange} alt="" /></i>
  <a
    href={`/contact?type=04.お届け内容の変更&content=${encodeURIComponent(
      `
【ご契約中の商品一覧】
${group.items
  .map(
    (item) => `
商品名：${item.product_name}
数量：${item.quantity}${getProductUnit(item.product_name)}（${item.cycle}）
単価：${item.unit_price.toLocaleString()}円（税込）
総額：${item.line_amount.toLocaleString()}円（税込）
`
  )
  .join("")}

【ご契約情報】

商品合計：${firstItem.total_amount.toLocaleString()}円（税込）
送料：${firstItem.shipping_amount.toLocaleString()}円
ご請求金額：${firstItem.billing_amount.toLocaleString()}円（税込）
支払い方法：${firstItem.payment_method_name}
ポイント利用：${
        firstItem.point_usage_flag === "する" && firstItem.usage_points > 0
          ? `${firstItem.usage_points}ポイントたまったら利用する`
          : "利用しない"
      }
お届け予定日：${firstItem.next_delivery_date || "未定"}
お届け時間：${firstItem.delivery_time_name || "時間指定なし"}
お届け先：${firstItem.recipient_name}

住所：〒${firstItem.recipient_postal}
${firstItem.recipient_prefecture_name}${firstItem.recipient_municipality_name}${firstItem.recipient_town_name}
${firstItem.recipient_address_detail_1}${firstItem.recipient_address_detail_2}

-------------------------------------------
上記、お届け内容の変更を希望します
      `
    )}`}
    className="underline bold"
  >
    お届け内容の変更
  </a>
</div>
                  )}

{/* ▼ 出荷予定日が当日の場合の注意メッセージ */}
{(() => {
  const today = new Date().toISOString().slice(0, 10);
  const ssd = firstItem.next_shipping_date?.slice(0, 10);
  return ssd === today;
})() && (
  <p style={{ color: "red", fontWeight: "bold", fontSize: "13px", marginTop: "8px" }}>
    ※出荷予定日が本日となっております。<br />お届け内容を変更されたい場合は、0120-41-8504へご連絡ください。
  </p>
)}

                  <div className="btn-orange-2">
                    <a href={`/use/${firstItem.subscription_number}`}>
                      <i><img src={iconPointOrange} alt="ポイントアイコン" /></i>
                      ポイント利用で「お得」に購入
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default Regular;
